/**
 * One implementation of "release everything, then report the first failure".
 *
 * `q-dispose-steps-unguarded-internally` (H-258): the constructor rollback
 * guards BETWEEN its teardown steps, but every `dispose()` in the Game family
 * was a bare sequence — a throw at step 1 stranded every resource behind it.
 * That is the release path a SUCCESSFUL instance takes, so it is the path that
 * actually holds a full set of resources to strand.
 *
 * Semantics, chosen deliberately and different from `rollbackConstruction()`:
 *
 * - **Every step runs.** A step that throws never prevents a later one.
 * - **The FIRST error is rethrown after the last step.** Swallowing outright
 *   would turn a real teardown bug into silence; rethrowing keeps the caller's
 *   existing contract (`App.tsx` already lets a `dispose()` throw propagate out
 *   of its effect cleanup), while the release itself is now complete.
 *
 * `rollbackConstruction()` keeps its own guard and deliberately swallows,
 * because there the construction error that caused the unwind is strictly more
 * informative than any teardown failure during it. The two are different
 * semantics, not a duplicated one.
 */
export function runTeardown(steps: ReadonlyArray<() => void>): void {
  let failed = false;
  let firstError: unknown;
  for (const step of steps) {
    try {
      step();
    } catch (error) {
      if (!failed) {
        failed = true;
        firstError = error;
      }
    }
  }
  if (failed) throw firstError;
}
